import { useEffect, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { toast } from "react-toastify";

import { useAuth } from "../../contexts/AuthContext";
import apiService from "../../services/api";

const ModuleAuditLogsModal = ({ onClose, open }) => {
  const { user } = useAuth();

  const [resellers, setResellers] = useState([]);

  const [moduleLogs, setModuleLogs] = useState([]);
  const [moduleLogsLoading, setModuleLogsLoading] = useState(false);
  const [moduleLogSearchTerm, setModuleLogSearchTerm] = useState("");
  const [moduleLogSelectedReseller, setModuleLogSelectedReseller] =
    useState("all");
  const [moduleLogResellerSearchTerm, setModuleLogResellerSearchTerm] =
    useState("");
  const [moduleLogSortField, setModuleLogSortField] = useState("createdAt");
  const [moduleLogSortDirection, setModuleLogSortDirection] = useState("desc");
  const [moduleLogCurrentPage, setModuleLogCurrentPage] = useState(1);
  const [moduleLogItemsPerPage, setModuleLogItemsPerPage] = useState(10);
  const [moduleLogPagination, setModuleLogPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0,
  });
  const [showModuleResellerDropdown, setShowModuleResellerDropdown] =
    useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      try {
        if (user?.role === "admin") {
          const response = await apiService.getResellers({ limit: 100 });

          if (cancelled) return;
          const list = response?.data?.resellers || [];

          setResellers(Array.isArray(list) ? list : []);
        } else {
          const response = await apiService.getMyReseller();

          if (cancelled) return;
          const r = response?.data?.reseller;

          setResellers(r ? [r] : []);
        }
      } catch (err) {
        console.error("Error loading reseller list for filter", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, user?.role]);

  const fetchModuleLogs = async () => {
    setModuleLogsLoading(true);
    try {
      const sortFieldMap = {
        reseller: "created_at",
        eventType: "event_type",
        status: "status",
        description: "created_at",
        createdAt: "created_at",
      };

      const apiSortField = sortFieldMap[moduleLogSortField] || "created_at";
      const apiSortOrder = moduleLogSortDirection.toUpperCase();

      const params = {
        page: moduleLogCurrentPage,
        limit: moduleLogItemsPerPage,
        sort: apiSortField,
        order: apiSortOrder,
      };

      if (moduleLogSearchTerm && moduleLogSearchTerm.trim()) {
        params.search = moduleLogSearchTerm.trim();
      }

      if (moduleLogSelectedReseller && moduleLogSelectedReseller !== "all") {
        const selectedResellerObj = resellers.find(
          (r) => r.username === moduleLogSelectedReseller
        );

        if (selectedResellerObj) {
          params.resellerId = selectedResellerObj.id;
        }
      }

      const response = await apiService.getModuleAuditLogs(params);
      const responseData = response.data || {};
      const logsData = responseData.logs || [];
      const paginationData = response.pagination || {
        total: 0,
        page: 1,
        limit: moduleLogItemsPerPage,
        pages: 0,
      };

      setModuleLogs(Array.isArray(logsData) ? logsData : []);
      setModuleLogPagination(paginationData);
    } catch (error) {
      console.error("Error fetching module audit logs:", error);
      toast.error("Failed to load module audit logs");
    } finally {
      setModuleLogsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchModuleLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    moduleLogCurrentPage,
    moduleLogItemsPerPage,
    moduleLogSortField,
    moduleLogSortDirection,
    moduleLogSelectedReseller,
  ]);

  useEffect(() => {
    if (open) {
      const timeoutId = setTimeout(() => {
        if (moduleLogCurrentPage !== 1) {
          setModuleLogCurrentPage(1);
        } else {
          fetchModuleLogs();
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleLogSearchTerm]);

  const uniqueLogResellers = [...resellers].map((r) => r.username).sort();

  const filteredModuleDropdownResellers = uniqueLogResellers.filter(
    (reseller) =>
      reseller.toLowerCase().includes(moduleLogResellerSearchTerm.toLowerCase())
  );

  const handleModuleLogSort = (field) => {
    if (moduleLogSortField === field) {
      setModuleLogSortDirection(
        moduleLogSortDirection === "asc" ? "desc" : "asc"
      );
    } else {
      setModuleLogSortField(field);
      setModuleLogSortDirection("asc");
    }
  };

  const currentModuleLogItems = moduleLogs;
  const moduleLogTotalPages = moduleLogPagination.pages;
  const moduleLogIndexOfFirstItem =
    (moduleLogPagination.page - 1) * moduleLogPagination.limit;
  const moduleLogIndexOfLastItem = Math.min(
    moduleLogIndexOfFirstItem + moduleLogs.length,
    moduleLogPagination.total
  );

  const getModuleLogPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (moduleLogTotalPages <= maxVisiblePages) {
      for (let i = 1; i <= moduleLogTotalPages; i++) {
        pages.push(i);
      }
    } else {
      if (moduleLogCurrentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(moduleLogTotalPages);
      } else if (moduleLogCurrentPage >= moduleLogTotalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = moduleLogTotalPages - 3; i <= moduleLogTotalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (
          let i = moduleLogCurrentPage - 1;
          i <= moduleLogCurrentPage + 1;
          i++
        ) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(moduleLogTotalPages);
      }
    }

    return pages;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          onClick={() => onClose()}
        >
          <motion.div
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
            exit={{ scale: 0.9, opacity: 0 }}
            initial={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Module Audit Logs
              </h2>
              <button
                className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                onClick={() => onClose()}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col lg:flex-row gap-4 mb-4">
              <div className="flex-1">
                <div className="relative">
                  <input
                    className="input pl-10 pr-10 w-full"
                    placeholder="Search logs..."
                    type="text"
                    value={moduleLogSearchTerm}
                    onChange={(e) => {
                      setModuleLogSearchTerm(e.target.value);
                      setModuleLogCurrentPage(1);
                    }}
                  />
                  <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  {moduleLogSearchTerm && (
                    <button
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      onClick={() => {
                        setModuleLogSearchTerm("");
                        setModuleLogCurrentPage(1);
                      }}
                    >
                      <i className="fas fa-times text-sm" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                {/* Searchable Reseller Filter */}
                <div className="relative w-full sm:w-auto">
                  <div
                    className="input w-full sm:w-64 cursor-pointer flex items-center justify-between"
                    onClick={() =>
                      setShowModuleResellerDropdown(!showModuleResellerDropdown)
                    }
                  >
                    <span
                      className={
                        moduleLogSelectedReseller === "all"
                          ? "text-gray-500"
                          : ""
                      }
                    >
                      {moduleLogSelectedReseller === "all"
                        ? "All Resellers"
                        : moduleLogSelectedReseller}
                    </span>
                    <i
                      className={`fas fa-chevron-${showModuleResellerDropdown ? "up" : "down"} text-gray-400 ml-2`}
                    />
                  </div>
                  {showModuleResellerDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-64 overflow-hidden">
                      <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                        <div className="relative">
                          <input
                            className="input w-full text-sm pr-8"
                            placeholder="Search resellers..."
                            type="text"
                            value={moduleLogResellerSearchTerm}
                            onChange={(e) =>
                              setModuleLogResellerSearchTerm(e.target.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                          {moduleLogResellerSearchTerm && (
                            <button
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                setModuleLogResellerSearchTerm("");
                              }}
                            >
                              <i className="fas fa-times text-sm" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        <div
                          className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                          onClick={() => {
                            setModuleLogSelectedReseller("all");
                            setModuleLogCurrentPage(1);
                            setShowModuleResellerDropdown(false);
                            setModuleLogResellerSearchTerm("");
                          }}
                        >
                          All Resellers
                        </div>
                        {filteredModuleDropdownResellers.length === 0 ? (
                          <div className="px-4 py-2 text-sm text-gray-500">
                            No resellers found
                          </div>
                        ) : (
                          filteredModuleDropdownResellers.map((reseller) => (
                            <div
                              className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                              key={reseller}
                              onClick={() => {
                                setModuleLogSelectedReseller(reseller);
                                setModuleLogCurrentPage(1);
                                setShowModuleResellerDropdown(false);
                                setModuleLogResellerSearchTerm("");
                              }}
                            >
                              {reseller}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Items Per Page */}
                <select
                  className="input w-full sm:w-auto"
                  value={moduleLogItemsPerPage}
                  onChange={(e) => {
                    setModuleLogItemsPerPage(Number(e.target.value));
                    setModuleLogCurrentPage(1);
                  }}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
            </div>

            {/* Logs Table */}
            <div className="flex-1 overflow-auto">
              <table className="w-full min-w-max">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900">
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => handleModuleLogSort("reseller")}
                    >
                      <div className="flex items-center gap-1">
                        Reseller
                        {moduleLogSortField === "reseller" && (
                          <i
                            className={`fas fa-chevron-${moduleLogSortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => handleModuleLogSort("eventType")}
                    >
                      <div className="flex items-center gap-1">
                        Event Type
                        {moduleLogSortField === "eventType" && (
                          <i
                            className={`fas fa-chevron-${moduleLogSortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => handleModuleLogSort("status")}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {moduleLogSortField === "status" && (
                          <i
                            className={`fas fa-chevron-${moduleLogSortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => handleModuleLogSort("description")}
                    >
                      <div className="flex items-center gap-1">
                        Description
                        {moduleLogSortField === "description" && (
                          <i
                            className={`fas fa-chevron-${moduleLogSortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => handleModuleLogSort("createdAt")}
                    >
                      <div className="flex items-center gap-1">
                        Date
                        {moduleLogSortField === "createdAt" && (
                          <i
                            className={`fas fa-chevron-${moduleLogSortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                          />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {moduleLogsLoading ? (
                    <tr>
                      <td className="text-center py-12" colSpan="5">
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                          <span className="text-gray-500">Loading logs...</span>
                        </div>
                      </td>
                    </tr>
                  ) : currentModuleLogItems.length === 0 ? (
                    <tr>
                      <td
                        className="text-center py-12 text-gray-500"
                        colSpan="5"
                      >
                        No logs found
                      </td>
                    </tr>
                  ) : (
                    currentModuleLogItems.map((log, index) => (
                      <tr
                        className={`${
                          index % 2 === 0
                            ? "bg-gray-50 dark:bg-gray-800/50"
                            : "bg-white dark:bg-gray-900/50"
                        } hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors`}
                        key={log.id}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {log.reseller}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                            {log.eventType}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              log.status === "success"
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                            }`}
                          >
                            {log.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {log.description}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {new Date(log.createdAt).toLocaleString()}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {moduleLogPagination.total > 0 && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {moduleLogIndexOfFirstItem + 1} to{" "}
                  {moduleLogIndexOfLastItem} of {moduleLogPagination.total} logs
                </p>
                <div className="flex gap-2">
                  <button
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={moduleLogCurrentPage === 1}
                    onClick={() =>
                      setModuleLogCurrentPage(moduleLogCurrentPage - 1)
                    }
                  >
                    Previous
                  </button>
                  {getModuleLogPageNumbers().map((page, index) =>
                    page === "..." ? (
                      <span
                        className="px-2 py-1 text-sm text-gray-500"
                        key={`module-ellipsis-${index}`}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        className={`px-3 py-1 text-sm border rounded-lg transition-colors ${
                          moduleLogCurrentPage === page
                            ? "bg-primary-600 text-white border-primary-600"
                            : "border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                        }`}
                        key={page}
                        onClick={() => setModuleLogCurrentPage(page)}
                      >
                        {page}
                      </button>
                    )
                  )}
                  <button
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={moduleLogCurrentPage === moduleLogTotalPages}
                    onClick={() =>
                      setModuleLogCurrentPage(moduleLogCurrentPage + 1)
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ModuleAuditLogsModal;
